// @ts-nocheck
/**
 * Product Builder — shared audio engine (record format, playback, upload prep).
 * Used by pb-audio-recorder.js and pb-longtext-recorder.js.
 * Recording navigation locks delegate to pb-flow-lock.js (single owner).
 */
(function () {
  'use strict';

  var RECORD_MIME_CANDIDATES = [
    'audio/mp4',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4;codecs="mp4a.40.2"',
    'audio/aac',
    'audio/webm;codecs=opus',
    'audio/webm'
  ];

  var players = new Set();
  var playerSeq = 0;
  var fieldLifecycles = new Set();

  // A recording, decode, upload and preview share one immutable field owner.
  function createFieldLifecycle(wrap, cancel, dispose) {
    var revision = 0;
    var disposed = false;
    function context() {
      var state = window.builderState;
      return state && wrap.closest('#sub-flow-container') ? state.sub :
        state && wrap.closest('#main-flow-container') ? state.main : window.productBuilder?.getActiveContext?.();
    }
    var lifecycle = {
      begin: function () {
        var owner = context();
        return { revision: ++revision, context: owner, selections: owner?.selections,
          currentId: owner?.currentId, state: window.builderState, product: window.BUILDER_DATA?.mainProduct };
      },
      current: function (operation) {
        return !!operation && !disposed && wrap.isConnected !== false && operation.revision === revision &&
          operation.state === window.builderState && operation.product === window.BUILDER_DATA?.mainProduct &&
          operation.context === context() && operation.selections === operation.context?.selections &&
          operation.currentId === operation.context?.currentId;
      },
      invalidate: function () { revision++; },
      cancel: function () { revision++; cancel(); },
      dispose: function () {
        if (disposed) return;
        disposed = true; revision++;
        fieldLifecycles.delete(lifecycle);
        cancel(); if (dispose) dispose();
      },
      wrap: wrap
    };
    fieldLifecycles.add(lifecycle);
    return lifecycle;
  }

  function disposeFieldScope(root) {
    fieldLifecycles.forEach(function (life) {
      if (life.wrap.isConnected === false || root === life.wrap || root?.contains?.(life.wrap)) life.dispose();
    });
  }

  function cancelFieldOperations() {
    fieldLifecycles.forEach(function (life) {
      if (life.wrap.isConnected === false) life.dispose();
      else life.cancel();
    });
  }


  function flowLock() {
    return typeof window !== 'undefined' ? window.PBFlowLock : null;
  }

  function recordingKind() {
    var fl = flowLock();
    return fl && fl.LOCKS ? fl.LOCKS.RECORDING : 'recording';
  }

  function registerRecordingSession(scopeEl) {
    var fl = flowLock();
    if (!fl || typeof fl.acquire !== 'function') return null;
    var token = fl.acquire(recordingKind(), { scopeEl: scopeEl || null });
    return { scopeEl: scopeEl || null, token: token };
  }

  function unregisterRecordingSession(session) {
    if (!session || !session.token) return;
    var fl = flowLock();
    if (fl && typeof fl.release === 'function') fl.release(session.token);
    session.token = null;
  }

  function isRecordingActive() {
    var fl = flowLock();
    if (fl && typeof fl.hasActiveLocks === 'function') return fl.hasActiveLocks(recordingKind());
    return false;
  }

  function isRecordingActiveIn(container) {
    var fl = flowLock();
    if (fl && typeof fl.isLockActiveIn === 'function') return fl.isLockActiveIn(container, recordingKind());
    return false;
  }

  function blocksActiveStepNavigation() {
    var fl = flowLock();
    if (fl && typeof fl.blocksActiveStepNavigation === 'function') return fl.blocksActiveStepNavigation();
    return isRecordingActive();
  }

  function resolveActiveStepElement() {
    var fl = flowLock();
    if (fl && typeof fl.resolveActiveStepElement === 'function') return fl.resolveActiveStepElement();
    return null;
  }

  function resolveActiveFlowRoot() {
    var fl = flowLock();
    if (fl && typeof fl.resolveActiveFlowRoot === 'function') return fl.resolveActiveFlowRoot();
    return null;
  }

  function canPlayMime(mime) {
    if (!mime) return false;
    try {
      var probe = document.createElement('audio');
      var answer = probe.canPlayType(mime);
      return answer === 'probably' || answer === 'maybe';
    } catch (e) {
      return false;
    }
  }

  function canRecordMime(mime) {
    return !!(window.MediaRecorder && window.MediaRecorder.isTypeSupported(mime));
  }

  function pickRecordMime() {
    for (var i = 0; i < RECORD_MIME_CANDIDATES.length; i++) {
      var mime = RECORD_MIME_CANDIDATES[i];
      if (canRecordMime(mime) && canPlayMime(mime)) return mime;
    }
    for (var j = 0; j < RECORD_MIME_CANDIDATES.length; j++) {
      if (canRecordMime(RECORD_MIME_CANDIDATES[j])) return RECORD_MIME_CANDIDATES[j];
    }
    return '';
  }

  function extensionForMime(mime) {
    var type = String(mime || '').toLowerCase();
    if (type.indexOf('mp4') !== -1 || type.indexOf('aac') !== -1 || type.indexOf('m4a') !== -1) return 'm4a';
    if (type.indexOf('webm') !== -1) return 'webm';
    if (type.indexOf('ogg') !== -1) return 'ogg';
    if (type.indexOf('wav') !== -1) return 'wav';
    return 'm4a';
  }

  function normalizeAudioBlob(blob, mimeType) {
    if (!blob) return blob;
    var type = mimeType || blob.type || pickRecordMime() || 'audio/mp4';
    if (blob.type === type) return blob;
    try {
      return new Blob([blob], { type: type });
    } catch (e) {
      return blob;
    }
  }

  function defaultRecordingFilename(blob) {
    var ext = extensionForMime(blob && blob.type);
    return 'recording.' + ext;
  }

  function sameAudioUrl(audio, url) {
    if (!audio || !url) return false;
    try {
      return audio.src === url || audio.src === new URL(url, window.location.href).href;
    } catch (e) {
      return audio.src.indexOf(url) !== -1;
    }
  }

  function revokeObjectUrl(url) {
    if (url && String(url).indexOf('blob:') === 0) {
      try { URL.revokeObjectURL(url); } catch (e) {}
    }
  }

  function stopAllPlayersExcept(exceptPlayer) {
    players.forEach(function (player) {
      if (player !== exceptPlayer) player.pause(true);
    });
  }

  function decodeBlobPeaks(blob) {
    var WF = window.pbAudioWaveform;
    if (!WF || !WF.decodePeaks) {
      return Promise.resolve({ peaks: [], duration: 0 });
    }
    return WF.decodePeaks(blob);
  }

  function probeBlobPlayback(blob) {
    if (!blob) return Promise.resolve(false);
    var url = URL.createObjectURL(blob);
    var audio = new Audio();
    audio.preload = 'auto';
    return new Promise(function (resolve) {
      var settled = false;
      function done(ok) {
        if (settled) return;
        settled = true;
        audio.src = '';
        revokeObjectUrl(url);
        resolve(!!ok);
      }
      audio.addEventListener('canplaythrough', function () { done(true); }, { once: true });
      audio.addEventListener('loadeddata', function () { done(true); }, { once: true });
      audio.addEventListener('error', function () { done(false); }, { once: true });
      audio.src = url;
      audio.load();
      setTimeout(function () {
        if (!settled && audio.readyState >= 2) done(true);
        else if (!settled) done(false);
      }, 1800);
    });
  }

  function createPlayer(options) {
    options = options || {};
    var id = 'pbap-' + (++playerSeq);
    var WF = window.pbAudioWaveform;
    var audio = new Audio();
    audio.preload = 'metadata';
    var peaks = null;
    var duration = 0;
    var objectUrl = '';
    var remoteUrl = '';
    var playRaf = null;
    var playSessionId = 0;
    var isPlaying = false;
    var playBusy = false;

    function syncDurationFromAudio() {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        duration = audio.duration;
      }
    }

    audio.addEventListener('loadedmetadata', syncDurationFromAudio);
    audio.addEventListener('durationchange', syncDurationFromAudio);

    function setObjectSource(blob) {
      revokeObjectUrl(objectUrl);
      objectUrl = blob ? URL.createObjectURL(blob) : '';
      remoteUrl = '';
      if (objectUrl) {
        audio.src = objectUrl;
        try { audio.load(); } catch (e) {}
      }
    }

    function setRemoteSource(url) {
      revokeObjectUrl(objectUrl);
      objectUrl = '';
      remoteUrl = url || '';
      if (url && !sameAudioUrl(audio, url)) {
        audio.src = url;
        try { audio.load(); } catch (e) {}
      }
    }

    function clearSource() {
      playSessionId++;
      pause(true);
      revokeObjectUrl(objectUrl);
      objectUrl = '';
      remoteUrl = '';
      peaks = null;
      duration = 0;
      audio.removeAttribute('src');
      try { audio.load(); } catch (e2) {}
    }

    function getDuration() {
      syncDurationFromAudio();
      return duration && isFinite(duration) && duration > 0 ? duration : 0;
    }

    function getProgress() {
      var dur = getDuration();
      var t = audio ? audio.currentTime : 0;
      return dur > 0 ? Math.min(1, Math.max(0, t / dur)) : 0;
    }

    function drawProgress(progress) {
      if (typeof options.onProgress === 'function') {
        options.onProgress(typeof progress === 'number' ? progress : getProgress(), peaks);
      } else if (WF && options.canvas && peaks) {
        WF.drawBars(options.canvas, peaks, typeof progress === 'number' ? progress : getProgress());
      }
    }

    function stopLoop() {
      if (playRaf) cancelAnimationFrame(playRaf);
      playRaf = null;
    }

    function startLoop(session) {
      stopLoop();
      function tick() {
        if (playSessionId !== session || !isPlaying) return;
        drawProgress(getProgress());
        if (!audio.paused && !audio.ended) playRaf = requestAnimationFrame(tick);
      }
      tick();
    }

    function setPlayState(playing) {
      isPlaying = !!playing;
      if (typeof options.onPlayStateChange === 'function') {
        options.onPlayStateChange(isPlaying);
      }
    }

    function pause(resetPosition) {
      playSessionId++;
      stopLoop();
      playBusy = false;
      isPlaying = false;
      audio.onended = null;
      audio.pause();
      setPlayState(false);
      if (resetPosition) {
        try { audio.currentTime = 0; } catch (e) {}
      }
    }

    function playFromCurrent() {
      var sourceUrl = remoteUrl || objectUrl;
      if (!sourceUrl || playBusy) {
        return Promise.resolve(false);
      }

      playBusy = true;
      stopAllPlayersExcept(player);

      var session = playSessionId + 1;
      playSessionId = session;

      var dur = getDuration();
      if (dur > 0 && audio.currentTime >= dur - 0.05) {
        try { audio.currentTime = 0; } catch (e) {}
      }

      audio.onended = function () {
        if (playSessionId !== session) return;
        pause(true);
        drawProgress(0);
      };

      return audio.play().then(function () {
        if (playSessionId !== session) return false;
        playBusy = false;
        isPlaying = true;
        setPlayState(true);
        startLoop(session);
        return true;
      }).catch(function (err) {
        if (playSessionId !== session) return false;
        playBusy = false;
        isPlaying = false;
        setPlayState(false);
        if (typeof options.onError === 'function') {
          options.onError(err || new Error('Playback failed'));
        }
        return false;
      });
    }

    function toggle() {
      if (isPlaying) {
        pause(false);
        drawProgress(getProgress());
        return Promise.resolve(true);
      }
      return playFromCurrent();
    }

    function seekToRatio(ratio) {
      var dur = getDuration();
      if (!dur) return;
      var t = Math.max(0, Math.min(dur, ratio * dur));
      try { audio.currentTime = t; } catch (e) {}
      drawProgress(ratio);
    }

    function bindSeek(canvas, canSeek) {
      if (!canvas || canvas.dataset.pbAeSeekBound) return;
      canvas.dataset.pbAeSeekBound = '1';
      canvas.style.touchAction = 'none';

      var dragging = false;
      var wasPlaying = false;
      var pointerId = null;
      var startX = 0;
      var moved = false;
      var lastRatio = 0;

      function ratioFromEvent(e) {
        if (WF && WF.seekRatioFromPointer) return WF.seekRatioFromPointer(canvas, e.clientX);
        var rect = canvas.getBoundingClientRect();
        return rect.width ? Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) : 0;
      }

      canvas.addEventListener('pointerdown', function (e) {
        if (typeof canSeek === 'function' && !canSeek()) return;
        dragging = true;
        moved = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        wasPlaying = isPlaying;
        if (wasPlaying) pause(false);
        try { canvas.setPointerCapture(pointerId); } catch (err) {}
        lastRatio = ratioFromEvent(e);
        seekToRatio(lastRatio);
        e.preventDefault();
      });

      canvas.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== pointerId) return;
        if (Math.abs(e.clientX - startX) > 4) moved = true;
        lastRatio = ratioFromEvent(e);
        seekToRatio(lastRatio);
      });

      function endScrub(e) {
        if (!dragging) return;
        dragging = false;
        try { canvas.releasePointerCapture(pointerId); } catch (err) {}
        pointerId = null;
        if (e && e.clientX != null) lastRatio = ratioFromEvent(e);
        seekToRatio(lastRatio);
        if (!moved || wasPlaying) playFromCurrent();
      }

      canvas.addEventListener('pointerup', endScrub);
      canvas.addEventListener('pointercancel', endScrub);
    }

    var player = {
      id: id,
      setBlob: function (blob, meta) {
        meta = meta || {};
        var normalized = normalizeAudioBlob(blob, meta.mimeType);
        setObjectSource(normalized);
        if (meta.peaks) peaks = meta.peaks;
        if (meta.duration) duration = meta.duration;
      },
      setRemoteUrl: function (url, meta) {
        meta = meta || {};
        setRemoteSource(url);
        if (meta.peaks) peaks = meta.peaks;
        if (meta.duration) duration = meta.duration;
      },
      setPeaks: function (nextPeaks, nextDuration) {
        peaks = nextPeaks || peaks;
        if (nextDuration) duration = nextDuration;
      },
      getPeaks: function () { return peaks; },
      getDuration: getDuration,
      getProgress: getProgress,
      drawProgress: drawProgress,
      toggle: toggle,
      play: playFromCurrent,
      pause: pause,
      stop: function () { pause(true); drawProgress(0); },
      seekToRatio: seekToRatio,
      bindSeek: bindSeek,
      clear: clearSource,
      destroy: function () {
        players.delete(player);
        clearSource();
        stopLoop();
      },
      isPlaying: function () { return isPlaying; }
    };

    players.add(player);
    return player;
  }

  function formatTime(sec) {
    var s = Math.max(0, Math.floor(sec || 0));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return (m < 10 ? '0' + m : m) + ':' + (r < 10 ? '0' + r : r);
  }

  function createRecorder(options) {
    options = options || {};
    var WF = window.pbAudioWaveform;
    var chunks = [];
    var mediaRecorder = null;
    var stream = null;
    var liveScroller = null;
    var liveRaf = null;
    var timerId = null;
    var recordingStart = 0;
    var selectedMime = '';
    var session = null;
    var terminal = false;
    var generation = 0;
    var startPromise = null;

    function endRecordingSession() {
      if (!session) return;
      unregisterRecordingSession(session);
      session = null;
    }

    function stopLive() {
      if (liveRaf) cancelAnimationFrame(liveRaf);
      liveRaf = null;
      if (liveScroller) {
        liveScroller.stop();
        liveScroller = null;
      }
    }

    function stopTimer() {
      if (timerId) clearInterval(timerId);
      timerId = null;
    }

    function cleanupStream() {
      if (stream) {
        stream.getTracks().forEach(function (t) { t.stop(); });
        stream = null;
      }
    }

    function finishWithBlob() {
      if (terminal) return;
      terminal = true;
      endRecordingSession();
      if (chunks.length === 0) {
        if (typeof options.onEmpty === 'function') options.onEmpty();
        return;
      }
      var mime = selectedMime || (mediaRecorder && mediaRecorder.mimeType) || pickRecordMime() || 'audio/mp4';
      var blob = normalizeAudioBlob(new Blob(chunks, { type: mime }), mime);
      if (typeof options.onBlob === 'function') options.onBlob(blob);
    }

    var api = {
      start: function () {
        if (startPromise || (mediaRecorder && !terminal)) return startPromise || Promise.resolve();
        var attempt = ++generation;
        chunks = [];
        terminal = false;
        selectedMime = pickRecordMime();
        if (!window.MediaRecorder || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          return Promise.reject(new Error('Recording is not supported in this browser.'));
        }
        session = registerRecordingSession(options.scopeEl || null);
        startPromise = Promise.resolve().then(function () {
          if (attempt !== generation || terminal) return null;
          return navigator.mediaDevices.getUserMedia({ audio: true });
        }).then(function (nextStream) {
          if (!nextStream) return;
          if (attempt !== generation || terminal || options.scopeEl?.isConnected === false) {
            nextStream.getTracks().forEach(function (track) { track.stop(); });
            if (attempt === generation) api.cancel();
            return;
          }
          stream = nextStream;
          try {
            mediaRecorder = selectedMime
              ? new MediaRecorder(stream, { mimeType: selectedMime })
              : new MediaRecorder(stream);
          } catch (e) {
            mediaRecorder = new MediaRecorder(stream);
          }
          selectedMime = mediaRecorder.mimeType || selectedMime || 'audio/mp4';

          mediaRecorder.ondataavailable = function (e) {
            if (attempt !== generation || terminal) return;
            if (e.data && e.data.size > 0) chunks.push(e.data);
          };

          mediaRecorder.onstop = function () {
            if (attempt !== generation || terminal) return;
            stopTimer();
            stopLive();
            cleanupStream();
            finishWithBlob();
          };

          mediaRecorder.start(200);
          recordingStart = Date.now();
          if (typeof options.onRecordingStart === 'function') options.onRecordingStart();

          timerId = setInterval(function () {
            var elapsed = (Date.now() - recordingStart) / 1000;
            if (typeof options.onTimer === 'function') {
              var formatted = WF && WF.formatTime ? WF.formatTime(elapsed) : formatTime(elapsed);
              options.onTimer(elapsed, formatted);
            }
            if (options.maxSec && elapsed >= options.maxSec) {
              api.stop();
            }
          }, 250);

          if (WF && WF.createLiveScroller && typeof options.onLiveFrame === 'function') {
            liveScroller = WF.createLiveScroller(stream);
            function drawLive() {
              if (!liveScroller) return;
              liveScroller.tick();
              options.onLiveFrame(liveScroller);
              liveRaf = requestAnimationFrame(drawLive);
            }
            drawLive();
          }
        }).catch(function (error) {
          if (attempt !== generation || terminal) return;
          terminal = true;
          stopTimer(); stopLive(); cleanupStream(); endRecordingSession();
          mediaRecorder = null;
          throw error;
        }).finally(function () { if (attempt === generation) startPromise = null; });
        return startPromise;
      },
      stop: function () {
        if (!mediaRecorder && startPromise) { api.cancel(); return; }
        stopTimer();
        /* Release lock synchronously — iOS onstop is unreliable. */
        endRecordingSession();
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          try { mediaRecorder.stop(); } catch (e) {}
        }
        var stoppingGeneration = generation;
        function settle() {
          if (terminal || stoppingGeneration !== generation) return;
          stopLive();
          cleanupStream();
          finishWithBlob();
        }
        if (terminal) return;
        /* If onstop already ran sync, terminal is set. Otherwise settle now/soon. */
        if (!mediaRecorder || mediaRecorder.state === 'inactive') {
          settle();
          return;
        }
        setTimeout(settle, 0);
      },
      cancel: function () {
        generation++;
        startPromise = null;
        if (terminal) {
          endRecordingSession();
          return;
        }
        terminal = true;
        stopTimer();
        stopLive();
        chunks = [];
        endRecordingSession();
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
          mediaRecorder.onstop = null;
          mediaRecorder.ondataavailable = null;
          try { mediaRecorder.stop(); } catch (e) {}
        } else {
          cleanupStream();
        }
        cleanupStream();
        mediaRecorder = null;
        if (typeof options.onCancel === 'function') options.onCancel();
      }
    };
    return api;
  }

  var PLAY_SVG = '<svg class="pb-lt-icon-play" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
  var PAUSE_SVG = '<svg class="pb-lt-icon-pause" width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" hidden><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/></svg>';
  var TRASH_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>';

  window.pbAudioEngine = {
    createFieldLifecycle: createFieldLifecycle,
    disposeFieldScope: disposeFieldScope,
    cancelFieldOperations: cancelFieldOperations,
    pickRecordMime: pickRecordMime,
    extensionForMime: extensionForMime,
    normalizeAudioBlob: normalizeAudioBlob,
    defaultRecordingFilename: defaultRecordingFilename,
    decodeBlobPeaks: decodeBlobPeaks,
    probeBlobPlayback: probeBlobPlayback,
    stopAllPlayersExcept: stopAllPlayersExcept,
    createPlayer: createPlayer,
    createRecorder: createRecorder,
    isRecordingActive: isRecordingActive,
    isRecordingActiveIn: isRecordingActiveIn,
    blocksActiveStepNavigation: blocksActiveStepNavigation,
    resolveActiveStepElement: resolveActiveStepElement,
    PLAY_SVG: PLAY_SVG,
    PAUSE_SVG: PAUSE_SVG,
    PLAY_BUTTON_HTML: PLAY_SVG + PAUSE_SVG,
    TRASH_SVG: TRASH_SVG
  };
})();
